import { useEffect, useMemo, useRef, useState } from 'react';
import { useMachine } from '@xstate/react';
import { BridgeFeed } from './components/BridgeFeed';
import { Composer } from './components/Composer';
import { OpinionMap } from './components/OpinionMap';
import { SeedForm } from './components/SeedForm';
import { SidePicker } from './components/SidePicker';
import { StatementCard } from './components/StatementCard';
import { createSpace, fetchConfig, submitStatement, submitVote, websocketUrl } from './lib/api';
import { getDeviceKey } from './lib/deviceKey';
import { pct, score } from './lib/format';
import { roundMachine } from './machines/roundMachine';
import { useAppStore } from './store';
import type { GroupId, SpaceSnapshot } from './types';

export default function App() {
  const [state, send] = useMachine(roundMachine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const wsRef = useRef<WebSocket | null>(null);

  const {
    config,
    deviceKey,
    snapshot,
    groupId,
    currentIndex,
    turnstileToken,
    presence,
    setConfig,
    setDeviceKey,
    setSnapshot,
    setGroupId,
    setTurnstileToken,
    setPresence,
    next
  } = useAppStore();

  useEffect(() => {
    fetchConfig().then(setConfig).catch((err: Error) => setError(err.message));
    getDeviceKey().then(setDeviceKey).catch((err: Error) => setError(err.message));
  }, [setConfig, setDeviceKey]);

  function connect(spaceId: string, key: string) {
    wsRef.current?.close();
    const ws = new WebSocket(websocketUrl(spaceId, key));
    wsRef.current = ws;
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data as string) as { type?: string; snapshot?: SpaceSnapshot; clients?: number };
      if (data.snapshot) setSnapshot(normalizeSnapshot(data.snapshot));
      if (typeof data.clients === 'number') setPresence(data.clients);
    };
    ws.onopen = () => ws.send(JSON.stringify({ type: 'refresh' }));
    ws.onclose = () => setTimeout(() => {
      if (deviceKey && snapshot?.space.id) connect(snapshot.space.id, deviceKey);
    }, 1800);
  }

  async function openSpace(seed: string, groupALabel: string, groupBLabel: string) {
    setBusy(true);
    setError(undefined);
    try {
      const snap = normalizeSnapshot(await createSpace(seed, groupALabel, groupBLabel));
      setSnapshot(snap);
      send({ type: 'SPACE_READY' });
      if (deviceKey) connect(snap.space.id, deviceKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create space');
    } finally {
      setBusy(false);
    }
  }

  function pickSide(group: GroupId) {
    setGroupId(group);
    send({ type: 'SIDE_PICKED' });
  }

  async function vote(agreement: number, confidence: number, guessOther: number, startedAt: number) {
    if (!snapshot || !deviceKey || !groupId || !currentStatement) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await submitVote({
        spaceId: snapshot.space.id,
        statementId: currentStatement.id,
        deviceKey,
        groupId,
        agreement,
        confidence,
        guessOther,
        reactionMs: Date.now() - startedAt,
        turnstileToken
      });
      if (result.snapshot) setSnapshot(normalizeSnapshot(result.snapshot));
      setTurnstileToken(undefined);
      window.turnstile?.reset();
      send({ type: 'VOTED' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Vote failed');
    } finally {
      setBusy(false);
    }
  }

  async function addStatement(text: string) {
    if (!snapshot || !deviceKey) return;
    setBusy(true);
    setError(undefined);
    try {
      await submitStatement({ spaceId: snapshot.space.id, text, deviceKey, turnstileToken });
      setTurnstileToken(undefined);
      window.turnstile?.reset();
      send({ type: 'SUBMITTED' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Statement failed');
    } finally {
      setBusy(false);
    }
  }

  const statements = snapshot?.statements || [];
  const currentStatement = statements.length ? statements[currentIndex % statements.length] : undefined;
  const topBridge = useMemo(() => statements.filter((s) => s.bridgeScore > 0).sort((a, b) => b.bridgeScore - a.bridgeScore)[0], [statements]);
  const groupLabel = snapshot && groupId ? (groupId === 'A' ? snapshot.space.groupALabel : snapshot.space.groupBLabel) : '';
  const otherLabel = snapshot && groupId ? (groupId === 'A' ? snapshot.space.groupBLabel : snapshot.space.groupALabel) : '';

  return (
    <main>
      <header className="hero">
        <nav>
          <div className="brand"><span />Hidden Commons</div>
          <div className="navRight">
            <div className="pill">Cloudflare free-tier build · no Vectorize · DO realtime</div>
            <div className="creatorLinks" aria-label="Creator links">
              <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer">LinkedIn</a>
              <a className="coffee" href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer">Buy me a coffee</a>
            </div>
          </div>
        </nav>
        <section className="heroGrid">
          <div>
            <p className="eyebrow">Pluralistic ignorance, but make it playable</p>
            <h1>Find the sentences both sides secretly agree on.</h1>
            <p className="lede">
              Vote privately. Guess the other side. When reality beats the stereotype, the map lights up. The commons keeps aggregates; you keep the little dopamine zap of being less doomed than expected. ✨
            </p>
          </div>
          <div className="statStack">
            <div><b>{snapshot?.space.voteCount || 0}</b><span>human signals</span></div>
            <div><b>{snapshot?.stats?.discovered || statements.filter((s) => s.status === 'discovered').length}</b><span>bridges found</span></div>
            <div><b>{presence || snapshot?.clients || 0}</b><span>live in room</span></div>
          </div>
        </section>
      </header>

      {error && <div className="error">{error}</div>}

      {!snapshot && <SeedForm loading={busy} onCreate={openSpace} />}

      {snapshot && state.matches('side') && <SidePicker snapshot={snapshot} onPick={pickSide} />}

      {snapshot && groupId && (
        <section className="workspace">
          <div className="leftRail">
            <div className="roomCard card">
              <span className="eyebrow">Room</span>
              <h2>{snapshot.space.seed}</h2>
              <p><b>A:</b> {snapshot.space.groupALabel} · <b>B:</b> {snapshot.space.groupBLabel}</p>
              <a href={`${apiOrigin()}/api/commons/${snapshot.space.id}`} target="_blank" rel="noreferrer">Open public commons JSON ↗</a>
            </div>
            <OpinionMap statements={statements} />
          </div>

          <div className="centerRail">
            {state.matches('vote') && currentStatement && (
              <StatementCard
                statement={currentStatement}
                groupId={groupId}
                groupLabel={groupLabel}
                otherLabel={otherLabel}
                siteKey={config?.turnstileSiteKey}
                token={turnstileToken}
                onToken={setTurnstileToken}
                onVote={vote}
                onCompose={() => send({ type: 'ADD_STATEMENT' })}
                busy={busy}
              />
            )}

            {state.matches('reveal') && (
              <section className="card reveal">
                <span className="eyebrow">Payoff unlocked</span>
                <h2>{topBridge ? 'A bridge is forming.' : 'Signal received.'}</h2>
                {topBridge ? (
                  <>
                    <blockquote>{topBridge.text}</blockquote>
                    <div className="revealStats">
                      <div><b>{score(topBridge.bridgeScore)}</b><span>bridge</span></div>
                      <div><b>{pct(topBridge.crossConsensus)}</b><span>cross-consensus</span></div>
                      <div><b>{pct(topBridge.surprise)}</b><span>underestimated</span></div>
                    </div>
                  </>
                ) : <p>Need a few more votes from both pulls before the app can reveal a legitimate agreement surprise.</p>}
                <div className="actions">
                  <button className="ghost" onClick={() => send({ type: 'ADD_STATEMENT' })}>Add bridge candidate</button>
                  <button className="primary" onClick={() => { next(); send({ type: 'NEXT' }); }}>Next statement</button>
                </div>
              </section>
            )}

            {state.matches('compose') && (
              <Composer
                siteKey={config?.turnstileSiteKey}
                token={turnstileToken}
                onToken={setTurnstileToken}
                onSubmit={addStatement}
                onCancel={() => send({ type: 'CANCEL' })}
                busy={busy}
              />
            )}
          </div>

          <BridgeFeed statements={statements} />
        </section>
      )}
    </main>
  );
}

function apiOrigin() {
  return (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '') || window.location.origin;
}

function normalizeSnapshot(snapshot: SpaceSnapshot): SpaceSnapshot {
  const anySnapshot = snapshot as unknown as { space?: Record<string, unknown>; statements?: unknown[]; topBridges?: unknown[] };
  const raw = anySnapshot.space || {};
  const space = {
    id: String(raw.id || ''),
    seed: String(raw.seed || ''),
    slug: String(raw.slug || ''),
    groupALabel: String(raw.groupALabel || raw.group_a_label || 'Move faster'),
    groupBLabel: String(raw.groupBLabel || raw.group_b_label || 'Go carefully'),
    createdAt: String(raw.createdAt || raw.created_at || ''),
    updatedAt: String(raw.updatedAt || raw.updated_at || ''),
    statementCount: Number(raw.statementCount || raw.statement_count || 0),
    voteCount: Number(raw.voteCount || raw.vote_count || 0)
  };
  return { ...snapshot, space };
}
